import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  LogOut, BookOpen, Award, Clock, CheckCircle, Volume2, Calendar, 
  ChevronDown, ChevronRight, Home, Video, Bell, Target, Trophy,
  BarChart3, FileText, Users, HelpCircle, Search, X, Eye, ArrowLeft,
  Heart, Smile, Meh, Frown, AlertTriangle, MessageCircle, Plus,
  TrendingUp, TrendingDown, Check, Loader2, Settings, Brain, GraduationCap, Sparkles, Menu, ClipboardList
} from 'lucide-react';
import { format } from 'date-fns';
import StudentInsights from '@/components/StudentInsights';
// StuckStudentAlerts removed — relies on attempts count which we deprecated
import HolidayCalendar from '@/components/HolidayCalendar';
import TeacherWeeklyAnalytics from '@/components/TeacherWeeklyAnalytics';
import TeacherSectionLeaderboard from '@/components/TeacherSectionLeaderboard';
import { LEAVE_TYPES, LEAVE_TYPE_LABELS, LEAVE_TYPE_COLORS } from '@/lib/leaveTypes';
import TeacherSkillAnalytics from './TeacherSkillAnalytics';
import TeacherSkillAssessmentReview from './TeacherSkillAssessmentReview';
import TeacherMockExams from './TeacherMockExams';
import BulkStudentUpload from './BulkStudentUpload';
import TeacherPracticeSuggestions from '@/components/TeacherPracticeSuggestions';
import ClassImprovementDashboard from '@/components/ClassImprovementDashboard';
import { generateDefaultPassword } from '@/lib/generateDefaultPassword';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Class Skill Insights Component for Teachers
const ClassSkillInsights = ({ classFilter }) => {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    loadInsights();
  }, [classFilter]);

  const loadInsights = async () => {
    setLoading(true);
    try {
      const classParam = classFilter && classFilter !== 'all' ? `?class_level=${classFilter}` : '';
      const response = await axios.get(`${API}/adaptive/teacher/class-insights${classParam}`);
      setInsights(response.data);
    } catch (error) {
      console.error('Error loading insights:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-12 text-center">
        <Loader2 className="mx-auto text-blue-500 mb-4 animate-spin" size={48} />
        <p className="text-gray-500">Loading class insights...</p>
      </div>
    );
  }

  if (!insights || insights.total_skills_tracked === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-12 text-center">
        <Brain className="mx-auto text-gray-300 mb-4" size={64} />
        <h3 className="text-xl font-semibold text-gray-600">No Skill Data Yet</h3>
        <p className="text-gray-400 mt-2">Students need to take diagnostic tests first</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <Brain className="text-purple-500 mb-2" size={32} />
          <p className="text-3xl font-bold text-gray-800">{insights.total_skills_tracked}</p>
          <p className="text-sm text-gray-500">Skills Tracked</p>
        </div>
        <div className={`rounded-xl p-6 shadow-sm ${insights.skills_needing_attention > 0 ? 'bg-red-50 border-2 border-red-200' : 'bg-green-50'}`}>
          <AlertTriangle className={insights.skills_needing_attention > 0 ? 'text-red-500 mb-2' : 'text-green-500 mb-2'} size={32} />
          <p className={`text-3xl font-bold ${insights.skills_needing_attention > 0 ? 'text-red-700' : 'text-green-700'}`}>
            {insights.skills_needing_attention}
          </p>
          <p className="text-sm text-gray-500">Skills Need Attention</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-6 shadow-sm">
          <Target className="text-blue-500 mb-2" size={32} />
          <p className="text-lg font-bold text-blue-700">{insights.class_level}</p>
          <p className="text-sm text-gray-500">Viewing Data For</p>
        </div>
      </div>

      {/* Problem Areas */}
      {insights.problem_areas && insights.problem_areas.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-400">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="text-red-500" size={20} />
            Skills Needing Class-wide Attention
          </h3>
          <p className="text-sm text-gray-500 mb-4">More than 50% of students are struggling with these skills</p>
          <div className="space-y-3">
            {insights.problem_areas.map((skill, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
                <div>
                  <p className="font-semibold text-gray-800">{skill.skill_name}</p>
                  <p className="text-sm text-gray-500">{skill.chapter_name}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-red-600 font-bold">{skill.weak_count}</p>
                    <p className="text-xs text-gray-400">Weak</p>
                  </div>
                  <div className="text-center">
                    <p className="text-yellow-600 font-bold">{skill.average_count}</p>
                    <p className="text-xs text-gray-400">Average</p>
                  </div>
                  <div className="text-center">
                    <p className="text-green-600 font-bold">{skill.strong_count}</p>
                    <p className="text-xs text-gray-400">Strong</p>
                  </div>
                  <Badge className="bg-red-100 text-red-700">{Math.round(skill.avg_accuracy)}% avg</Badge>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-gray-600 bg-yellow-50 p-3 rounded-lg">
            💡 <strong>Recommendation:</strong> Consider re-teaching these concepts with different approaches or additional practice sessions.
          </p>
        </div>
      )}

      {/* All Skills Breakdown */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="text-blue-500" size={20} />
          All Skills Performance
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-gray-600 text-sm">
              <tr>
                <th className="px-4 py-3 text-left">Skill</th>
                <th className="px-4 py-3 text-left">Chapter</th>
                <th className="px-4 py-3 text-center">Students</th>
                <th className="px-4 py-3 text-center">🟢 Strong</th>
                <th className="px-4 py-3 text-center">🟡 Average</th>
                <th className="px-4 py-3 text-center">🔴 Weak</th>
                <th className="px-4 py-3 text-center">Avg Score</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {insights.skill_breakdown?.map((skill, index) => (
                <tr key={index} className={skill.needs_attention ? 'bg-red-50' : ''}>
                  <td className="px-4 py-3 font-medium">{skill.skill_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{skill.chapter_name}</td>
                  <td className="px-4 py-3 text-center">{skill.total_students}</td>
                  <td className="px-4 py-3 text-center text-green-600 font-medium">{skill.strong_count}</td>
                  <td className="px-4 py-3 text-center text-yellow-600 font-medium">{skill.average_count}</td>
                  <td className="px-4 py-3 text-center text-red-600 font-medium">{skill.weak_count}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-bold ${
                      skill.avg_accuracy >= 75 ? 'text-green-600' :
                      skill.avg_accuracy >= 50 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {Math.round(skill.avg_accuracy)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {skill.needs_attention ? (
                      <Badge className="bg-red-100 text-red-700">Needs Focus</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Good</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recommendation Box */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
        <h3 className="font-bold text-gray-800 mb-2">📊 Summary</h3>
        <p className="text-gray-600">{insights.recommendation}</p>
      </div>
    </div>
  );
};

// Sidebar Menu Item Component
const MenuItem = ({ icon: Icon, label, active, onClick, hasSubmenu, isOpen, children, badge }) => {
  return (
    <div>
      <button
        onClick={onClick}
        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
          active 
            ? 'bg-blue-700 text-white' 
            : 'text-blue-100 hover:bg-blue-700/50'
        }`}
      >
        <div className="flex items-center gap-3">
          <Icon size={18} />
          <span className="font-medium text-sm uppercase tracking-wide">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {badge && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{badge}</span>
          )}
          {hasSubmenu && (
            <span className="text-blue-300">
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          )}
        </div>
      </button>
      {hasSubmenu && isOpen && (
        <div className="bg-blue-900/50">
          {children}
        </div>
      )}
    </div>
  );
};

const SubMenuItem = ({ label, active, onClick, badge = null }) => (
  <button
    onClick={onClick}
    className={`w-full px-8 py-2.5 text-left text-sm transition-colors flex items-center justify-between gap-2 ${
      active
        ? 'bg-blue-600 text-white'
        : 'text-blue-200 hover:bg-blue-700/30'
    }`}
  >
    <span className="truncate">{label}</span>
    {badge != null && badge > 0 && (
      <span className="bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 inline-flex items-center justify-center px-1.5 shrink-0">
        {badge}
      </span>
    )}
  </button>
);

const QuestionPreviewOverlay = ({ questionPreview, onClose }) => {
  const [activeTab, setActiveTab] = React.useState('Basic');
  const tabs = ['Basic', 'Intermediate', 'Advanced'];
  
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold">{questionPreview.chapter_name}</h2>
              <p className="text-blue-100 text-sm mt-1">Class {questionPreview.class_level} {questionPreview.subject} - {questionPreview.total_questions} Questions</p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="close-preview-btn">&times;</button>
          </div>
          <div className="flex gap-2 mt-4">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab ? 'bg-white text-blue-700 shadow-md' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid={`question-tab-${tab.toLowerCase()}`}
              >
                {tab} ({questionPreview.counts?.[tab] || 0})
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-y-auto p-6" style={{maxHeight: 'calc(85vh - 180px)'}}>
          {(questionPreview.difficulty_groups?.[activeTab] || []).length === 0 ? (
            <p className="text-gray-500 text-center py-8">No {activeTab.toLowerCase()} questions generated yet.</p>
          ) : (
            <div className="space-y-4">
              {(questionPreview.difficulty_groups?.[activeTab] || []).map((q, idx) => (
                <div key={q.id || idx} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={`question-card-${idx}`}>
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-100 text-blue-700 rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">{idx + 1}</span>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 leading-relaxed">{q.question}</p>
                      {q.options && (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {(Array.isArray(q.options) ? q.options.map((opt, i) => [String.fromCharCode(65+i), opt]) : Object.entries(q.options)).map(([key, val]) => (
                            <div key={key} className={`px-3 py-2 rounded-lg text-sm ${
                              key === q.correct || val === q.correct
                                ? 'bg-emerald-50 border-2 border-emerald-400 text-emerald-800 font-medium'
                                : 'bg-gray-50 border border-gray-200 text-gray-600'
                            }`}>
                              <span className="font-medium mr-2">{key}.</span>{val}
                            </div>
                          ))}
                        </div>
                      )}
                      {q.correct && !q.options && (
                        <div className="mt-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <span className="text-xs font-semibold text-emerald-600 uppercase">Answer: </span>
                          <span className="text-emerald-800">{q.correct}</span>
                        </div>
                      )}
                      {q.explanation && (
                        <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
                          <span className="text-xs font-semibold text-blue-600 uppercase">Explanation: </span>
                          <span className="text-blue-800 text-sm">{q.explanation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const NewTeacherDashboard = () => {
  const { user, logout } = useAuth();
  
  // Derive teacher's assigned subjects and classes
  const teacherSubjects = user?.subjects?.length ? user.subjects : ['Mathematics', 'Science', 'English', 'Social Studies'];
  const teacherClasses = user?.class_levels?.length ? user.class_levels : ['6', '7', '8', '9', '10'];
  
  // Navigation State
  const [activeSection, setActiveSection] = useState('dashboard');
  const [practiceSuggestionsCount, setPracticeSuggestionsCount] = useState(0);
  const [activeSubSection, setActiveSubSection] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({ progress: false, results: false });
  
  // Data State
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [examTypeFilter, setExamTypeFilter] = useState('all'); // all | topic_wise | unit | quarterly | half_yearly | annual
  const [resultsClassFilter, setResultsClassFilter] = useState('all'); // class filter inside Results section
  const [resultsSubjectFilter, setResultsSubjectFilter] = useState('all'); // subject filter inside Results section
  const [inactiveStudents, setInactiveStudents] = useState({ inactive_students: [], total: 0 });
  const [students, setStudents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [myLeaves, setMyLeaves] = useState([]);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_date: '', end_date: '', leave_type: '', reason: '' });
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [showClassForm, setShowClassForm] = useState(false);
  const [classForm, setClassForm] = useState({ title: '', subject: '', class_level: '', topic: '', meet_link: '', scheduled_at: '', duration_minutes: 45, description: '' });
  const [submittingClass, setSubmittingClass] = useState(false);
  const [classAttendance, setClassAttendance] = useState(null);
  const [showAttendance, setShowAttendance] = useState(false);
  const [syllabus, setSyllabus] = useState(null);
  const [mentalHealthOverview, setMentalHealthOverview] = useState(null);
  const [mentalHealthAlerts, setMentalHealthAlerts] = useState(null);
  
  // Filters — default to first assigned subject/class
  const [selectedClass, setSelectedClass] = useState(teacherClasses.length === 1 ? teacherClasses[0] : 'all');
  const [selectedSection, setSelectedSection] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState(teacherSubjects[0] || 'Mathematics');
  
  // Test Creation State
  const [testForm, setTestForm] = useState({
    title: '',
    class_level: '6',
    subject: 'Mathematics',
    category: 'Quarterly',
    topics: [],
    total_questions: 10,
    duration_minutes: 30,
    start_date: '',
    end_date: ''
  });
  const [creatingTest, setCreatingTest] = useState(false);
  const [coveredTopics, setCoveredTopics] = useState([]);
  
  // Detail Views
  const [selectedResult, setSelectedResult] = useState(null);
  const [resultDetails, setResultDetails] = useState(null);
  const [resultsModalView, setResultsModalView] = useState('insights'); // 'insights' | 'details'
  const [selectedStudentMH, setSelectedStudentMH] = useState(null);
  const [studentMHDetail, setStudentMHDetail] = useState(null);
  
  // Add Student State
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [addStudentForm, setAddStudentForm] = useState({
    name: '',
    roll_number: '',
    class_level: '6',
    section: 'A',
    username: '',
    password: generateDefaultPassword()
  });
  const [addingStudent, setAddingStudent] = useState(false);
  const [addedStudents, setAddedStudents] = useState([]);
  
  // Student Detail View State
  const [selectedStudentDetail, setSelectedStudentDetail] = useState(null);
  const [studentDetailData, setStudentDetailData] = useState(null);
  const [loadingStudentDetail, setLoadingStudentDetail] = useState(false);
  const [showQuestionPreview, setShowQuestionPreview] = useState(false);
  const [questionPreview, setQuestionPreview] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on mobile when navigating
  useEffect(() => {
    setSidebarOpen(false);
  }, [activeSection]);

  // Reset the global Class filter when switching sections so a class chosen
  // on (say) Students doesn't bleed into Leaves / Mental Health / etc.
  // For teachers assigned to exactly one class we keep that class pinned.
  useEffect(() => {
    setSelectedClass(teacherClasses.length === 1 ? teacherClasses[0] : 'all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Lightweight initial fetch of practice-suggestions count (for the sidebar badge)
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/teacher/practice-suggestions`)
      .then((r) => { if (!cancelled) setPracticeSuggestionsCount(r.data?.total || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeSection === 'syllabus') {
      // Force a specific class for syllabus (not "all")
      if (selectedClass === 'all' && teacherClasses.length > 0) {
        setSelectedClass(teacherClasses[0]);
        return; // Will re-trigger via selectedClass change
      }
      loadSyllabus();
    } else if (activeSection === 'mental-health') {
      loadMentalHealth();
    } else if (activeSection === 'online-classes') {
      loadOnlineClasses();
    } else if (activeSection === 'create-test') {
      loadCoveredTopics();
    }
  }, [activeSection, selectedClass, selectedSubject, testForm.class_level, testForm.subject, testForm.category]);

  // Auto-poll syllabus when any chapter is still generating
  useEffect(() => {
    if (activeSection !== 'syllabus' || !syllabus?.chapters) return;
    const hasGenerating = syllabus.chapters.some(ch => ch.generation_status === 'generating');
    if (!hasGenerating) return;
    const interval = setInterval(() => { loadSyllabus(); }, 4000);
    return () => clearInterval(interval);
  }, [activeSection, syllabus]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [resultsRes, studentsRes, analyticsRes, leavesRes, mhRes, myLeavesRes, inactiveRes] = await Promise.all([
        axios.get(`${API}/teacher/results`),
        axios.get(`${API}/teacher/students`),
        axios.get(`${API}/teacher/analytics/detailed`).catch(() => ({ data: null })),
        axios.get(`${API}/teacher/leaves`),
        axios.get(`${API}/teacher/mental-health/overview`).catch(() => ({ data: null })),
        axios.get(`${API}/teacher/my-leaves`).catch(() => ({ data: [] })),
        axios.get(`${API}/teacher/inactive-students?days=3`).catch(() => ({ data: { inactive_students: [], total: 0 } })),
      ]);
      setResults(resultsRes.data);
      setStudents(studentsRes.data);
      setAnalytics(analyticsRes.data);
      setLeaves(leavesRes.data);
      setMentalHealthOverview(mhRes.data);
      setMyLeaves(myLeavesRes.data || []);
      setInactiveStudents(inactiveRes.data || { inactive_students: [], total: 0 });
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadSyllabus = async () => {
    try {
      const classLevel = selectedClass === 'all' ? teacherClasses[0] : selectedClass;
      const response = await axios.get(`${API}/teacher/syllabus?class_level=${classLevel}&subject=${selectedSubject}`);
      setSyllabus(response.data);
      // Log for debugging
      console.log(`Loaded syllabus: class=${classLevel}, subject=${selectedSubject}, chapters=${response.data?.chapters?.length}, first_ch=${response.data?.chapters?.[0]?.name}`);
    } catch (error) {
      console.error('Error loading syllabus:', error);
      setSyllabus(null);
    }
  };

  const loadMentalHealth = async () => {
    try {
      const classParam = selectedClass !== 'all' ? `?class_level=${selectedClass}` : '';
      const [overviewRes, alertsRes] = await Promise.all([
        axios.get(`${API}/teacher/mental-health/overview${classParam}`),
        axios.get(`${API}/teacher/mental-health/alerts${classParam}`)
      ]);
      setMentalHealthOverview(overviewRes.data);
      setMentalHealthAlerts(alertsRes.data);
    } catch (error) {
      console.error('Error loading mental health:', error);
    }
  };

  const loadCoveredTopics = async () => {
    try {
      const response = await axios.get(`${API}/teacher/syllabus/covered-topics?class_level=${testForm.class_level}&subject=${testForm.subject}&term=${testForm.category}`);
      setCoveredTopics(response.data);
    } catch (error) {
      console.error('Error loading covered topics:', error);
    }
  };

  const loadResultDetails = async (attemptId) => {
    try {
      const response = await axios.get(`${API}/teacher/results/${attemptId}/details`);
      setResultDetails(response.data);
      setSelectedResult(attemptId);
    } catch (error) {
      toast.error('Failed to load result details');
    }
  };

  const loadStudentMHDetail = async (studentId) => {
    try {
      const response = await axios.get(`${API}/teacher/mental-health/student/${studentId}`);
      setStudentMHDetail(response.data);
      setSelectedStudentMH(studentId);
    } catch (error) {
      toast.error('Failed to load student details');
    }
  };

  const approveResult = async (resultId) => {
    try {
      await axios.patch(`${API}/teacher/results/${resultId}/approve`);
      toast.success('Result approved');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to approve result');
    }
  };

  const approveLeave = async (leaveId) => {
    try {
      await axios.patch(`${API}/teacher/leaves/${leaveId}/approve`);
      toast.success('Leave approved');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const rejectLeave = async (leaveId) => {
    try {
      await axios.patch(`${API}/teacher/leaves/${leaveId}/reject`);
      toast.success('Leave rejected');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  const loadMyLeaves = async () => {
    try {
      const res = await axios.get(`${API}/teacher/my-leaves`);
      setMyLeaves(res.data);
    } catch (error) {
      console.error('Error loading my leaves:', error);
    }
  };

  const submitLeave = async () => {
    if (!leaveForm.leave_date || !leaveForm.leave_type) {
      toast.error('Please pick a date and a leave type');
      return;
    }
    setSubmittingLeave(true);
    try {
      await axios.post(`${API}/teacher/my-leaves/apply`, {
        leave_date: new Date(leaveForm.leave_date).toISOString(),
        end_date: leaveForm.end_date ? new Date(leaveForm.end_date).toISOString() : null,
        leave_type: leaveForm.leave_type,
        reason: leaveForm.reason || LEAVE_TYPE_LABELS[leaveForm.leave_type] || leaveForm.leave_type
      });
      toast.success('Leave application submitted');
      setShowLeaveForm(false);
      setLeaveForm({ leave_date: '', end_date: '', leave_type: '', reason: '' });
      loadMyLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave');
    } finally {
      setSubmittingLeave(false);
    }
  };

  const cancelMyLeave = async (leaveId) => {
    try {
      await axios.delete(`${API}/teacher/my-leaves/${leaveId}`);
      toast.success('Leave cancelled');
      loadMyLeaves();
    } catch (error) {
      toast.error('Failed to cancel leave');
    }
  };

  const loadOnlineClasses = async () => {
    try {
      const res = await axios.get(`${API}/teacher/online-classes`);
      setOnlineClasses(res.data);
    } catch (error) {
      console.error('Error loading online classes:', error);
    }
  };

  const scheduleClass = async () => {
    if (!classForm.title || !classForm.meet_link || !classForm.scheduled_at || !classForm.subject || !classForm.class_level) {
      toast.error('Please fill in title, subject, class, meet link and date/time');
      return;
    }
    setSubmittingClass(true);
    try {
      await axios.post(`${API}/teacher/online-classes`, {
        ...classForm,
        scheduled_at: new Date(classForm.scheduled_at).toISOString()
      });
      toast.success('Online class scheduled!');
      setShowClassForm(false);
      setClassForm({ title: '', subject: '', class_level: '', topic: '', meet_link: '', scheduled_at: '', duration_minutes: 45, description: '' });
      loadOnlineClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule class');
    } finally {
      setSubmittingClass(false);
    }
  };

  const deleteOnlineClass = async (classId) => {
    if (!window.confirm('Delete this scheduled class?')) return;
    try {
      await axios.delete(`${API}/teacher/online-classes/${classId}`);
      toast.success('Class deleted');
      loadOnlineClasses();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const viewAttendance = async (classId) => {
    try {
      const res = await axios.get(`${API}/teacher/online-classes/${classId}/attendance`);
      setClassAttendance(res.data);
      setShowAttendance(true);
    } catch (error) {
      toast.error('Failed to load attendance');
    }
  };

  const addStudent = async () => {
    if (!addStudentForm.name || !addStudentForm.roll_number || !addStudentForm.username) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setAddingStudent(true);
    try {
      const response = await axios.post(`${API}/teacher/students/add`, addStudentForm);
      toast.success(`Student ${addStudentForm.name} added successfully!`);
      
      // Add to the list of added students (for showing credentials)
      setAddedStudents([...addedStudents, response.data.student]);
      
      // Reset form
      setAddStudentForm({
        name: '',
        roll_number: '',
        class_level: addStudentForm.class_level,
        section: addStudentForm.section,
        username: '',
        password: generateDefaultPassword()
      });
      
      // Refresh students list
      loadStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    } finally {
      setAddingStudent(false);
    }
  };

  const deleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to delete ${studentName}? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/teacher/students/${studentId}`);
      toast.success(`Student ${studentName} deleted`);
      loadStudents();
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  const viewStudentDetail = async (student) => {
    setSelectedStudentDetail(student);
    setLoadingStudentDetail(true);
    try {
      // Get student's test results - match by student.id (not user_id)
      const studentResults = results.filter(r => r.student_id === student.id);
      
      // Calculate statistics
      const totalTests = studentResults.length;
      const avgScore = totalTests > 0 
        ? Math.round(studentResults.reduce((sum, r) => sum + r.score, 0) / totalTests)
        : 0;
      const passedTests = studentResults.filter(r => r.score >= 50).length;
      
      // Get subject-wise breakdown
      const subjectStats = {};
      studentResults.forEach(r => {
        if (!subjectStats[r.subject]) {
          subjectStats[r.subject] = { count: 0, totalScore: 0 };
        }
        subjectStats[r.subject].count++;
        subjectStats[r.subject].totalScore += r.score;
      });
      
      // Try to get skill progress (may not exist for all students)
      let skillProgress = [];
      try {
        const skillRes = await axios.get(`${API}/adaptive/student/${student.user_id}/progress`);
        skillProgress = skillRes.data?.skills || [];
      } catch (e) {
        // Skill progress not available for this student
      }
      
      setStudentDetailData({
        student,
        results: studentResults,
        stats: {
          totalTests,
          avgScore,
          passedTests,
          failedTests: totalTests - passedTests
        },
        subjectStats,
        skillProgress
      });
    } catch (error) {
      console.error('Error loading student details:', error);
      toast.error('Failed to load student details');
    } finally {
      setLoadingStudentDetail(false);
    }
  };

  const closeStudentDetail = () => {
    setSelectedStudentDetail(null);
    setStudentDetailData(null);
  };

  const updateTopicStatus = async (topicId, status) => {
    try {
      await axios.patch(`${API}/teacher/syllabus/topic/${topicId}`, { status });
      toast.success('Topic updated');
      loadSyllabus();
    } catch (error) {
      toast.error('Failed to update topic');
    }
  };

  const createTest = async () => {
    if (!testForm.title || testForm.topics.length === 0) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setCreatingTest(true);
    try {
      await axios.post(`${API}/teacher/tests/create`, testForm);
      toast.success('Test created successfully!');
      setTestForm({
        title: '',
        class_level: '6',
        subject: 'Mathematics',
        category: 'Quarterly',
        topics: [],
        total_questions: 10,
        duration_minutes: 30,
        start_date: '',
        end_date: ''
      });
    } catch (error) {
      toast.error('Failed to create test');
    } finally {
      setCreatingTest(false);
    }
  };

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'bg-green-100 text-green-700';
    if (score >= 50) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const getMoodIcon = (mood) => {
    const icons = {
      happy: <Smile className="text-green-500" size={20} />,
      okay: <Meh className="text-blue-500" size={20} />,
      sad: <Frown className="text-yellow-500" size={20} />,
      stressed: <AlertTriangle className="text-orange-500" size={20} />,
      anxious: <AlertTriangle className="text-red-500" size={20} />
    };
    return icons[mood] || <Meh className="text-gray-400" size={20} />;
  };

  const pendingLeaves = leaves.filter(l => l.status === 'pending').length;
  const pendingApprovalsCount = results.filter(r => r.has_descriptive && !r.approved).length;
  const talkRequests = mentalHealthOverview?.talk_requests_count || 0;

  // Filter data by class + section
  const filteredStudents = students.filter(s => {
    if (selectedClass !== 'all' && s.class_level !== selectedClass) return false;
    if (selectedSection !== 'all' && (s.section || 'A') !== selectedSection) return false;
    return true;
  });

  // Section filter for results uses the linked student record
  const filteredResults = results
    .filter(r => {
      if (selectedClass !== 'all' && r.class_level !== selectedClass) return false;
      if (selectedSection !== 'all') {
        const student = students.find(s => s.id === r.student_id);
        if ((student?.section || 'A') !== selectedSection) return false;
      }
      return true;
    })
    .filter(r => examTypeFilter === 'all' ? true : (r.exam_type === examTypeFilter))
    .filter(r => resultsClassFilter === 'all' ? true : (r.class_level === resultsClassFilter))
    .filter(r => resultsSubjectFilter === 'all' ? true : (r.subject === resultsSubjectFilter));

  // Dedupe: only keep the LATEST attempt per (student_id, test_id) for the results listing.
  // Older attempts of the same test by the same student were cluttering the table.
  const dedupedResults = (() => {
    const latest = new Map();
    for (const r of filteredResults) {
      const key = `${r.student_id}::${r.test_id || r.test_title}`;
      const existing = latest.get(key);
      const rTime = new Date(r.completed_at || r.submitted_at || 0).getTime();
      const eTime = existing ? new Date(existing.completed_at || existing.submitted_at || 0).getTime() : -1;
      if (!existing || rTime >= eTime) {
        // Attach attempt count so we can show "(N attempts)" badge
        const prevCount = existing?._attemptCount || 1;
        latest.set(key, { ...r, _attemptCount: prevCount + (existing ? 0 : 0) + 1 });
      } else {
        existing._attemptCount = (existing._attemptCount || 1) + 1;
      }
    }
    return Array.from(latest.values()).sort((a, b) =>
      new Date(b.completed_at || b.submitted_at || 0) - new Date(a.completed_at || a.submitted_at || 0)
    );
  })();

  return (
    <div className="flex min-h-screen bg-gray-100" data-testid="teacher-dashboard">
      {/* Question Preview Modal */}
      {showQuestionPreview && questionPreview && (
        <QuestionPreviewOverlay 
          questionPreview={questionPreview} 
          onClose={() => setShowQuestionPreview(false)} 
        />
      )}
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-blue-800 to-blue-900 text-white flex flex-col shadow-xl transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        {/* Logo/Brand */}
        <div className="p-4 border-b border-blue-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <BookOpen className="text-blue-600" size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MyClassAI</h1>
              <p className="text-blue-300 text-xs">Teacher Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 overflow-y-auto">
          <MenuItem
            icon={Home}
            label="Dashboard"
            active={activeSection === 'dashboard'}
            onClick={() => setActiveSection('dashboard')}
          />

          {/* === INSIGHTS (most used) === */}
          <MenuItem
            icon={Sparkles}
            label="AI Practice Suggestions"
            active={activeSection === 'practice-suggestions'}
            onClick={() => setActiveSection('practice-suggestions')}
            badge={practiceSuggestionsCount}
          />

          <MenuItem
            icon={TrendingUp}
            label="Class Improvement"
            active={activeSection === 'class-improvement'}
            onClick={() => setActiveSection('class-improvement')}
          />

          <MenuItem
            icon={Brain}
            label="Student Review & AI Plan"
            active={activeSection === 'skill-review'}
            onClick={() => setActiveSection('skill-review')}
          />

          <MenuItem
            icon={GraduationCap}
            label="Class Overview"
            active={activeSection === 'skill-assessment'}
            onClick={() => setActiveSection('skill-assessment')}
          />

          <MenuItem
            icon={FileText}
            label="Results"
            active={activeSection === 'results' || expandedMenus.results}
            hasSubmenu
            isOpen={expandedMenus.results}
            onClick={() => toggleMenu('results')}
          >
            <SubMenuItem
              label="Auto-graded Results"
              active={activeSubSection === 'all-results'}
              onClick={() => { setActiveSection('results'); setActiveSubSection('all-results'); }}
            />
            <SubMenuItem
              label="Descriptive — Needs My Review"
              active={activeSubSection === 'pending'}
              onClick={() => { setActiveSection('results'); setActiveSubSection('pending'); }}
              badge={pendingApprovalsCount}
            />
          </MenuItem>

          <MenuItem
            icon={ClipboardList}
            label="Class Exams"
            active={activeSection === 'mock-exams'}
            onClick={() => setActiveSection('mock-exams')}
          />

          {/* === CLASSROOM === */}
          <MenuItem
            icon={CheckCircle}
            label="Attendance & Leaves"
            active={activeSection === 'attendance' || activeSection === 'leaves'}
            onClick={() => setActiveSection('attendance')}
            badge={pendingLeaves > 0 ? pendingLeaves : null}
          />

          <MenuItem
            icon={BookOpen}
            label="Syllabus"
            active={activeSection === 'syllabus'}
            onClick={() => setActiveSection('syllabus')}
          />

          {/* === PEOPLE === */}
          <MenuItem
            icon={HelpCircle}
            label="Support"
            active={activeSection === 'support'}
            onClick={() => setActiveSection('support')}
          />
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-blue-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{user?.name?.charAt(0) || 'T'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name || 'Teacher'}</p>
              <p className="text-blue-300 text-xs">Teacher</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-blue-200 hover:text-white hover:bg-blue-700"
            onClick={logout}
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto md:ml-0">
        {/* Top Bar */}
        <header className="bg-white shadow-sm px-4 md:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100"
                data-testid="mobile-menu-btn"
              >
                <Menu size={24} />
              </button>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {activeSection === 'dashboard' && 'Dashboard'}
                  {activeSection === 'practice-suggestions' && 'AI Practice Suggestions'}
                  {activeSection === 'results' && 'Test Results'}
                  {activeSection === 'skill-assessment' && 'Class Overview — Topic Tests'}
                  {activeSection === 'skill-review' && 'Student Review & AI Plan'}
                  {activeSection === 'mental-health' && 'Student Mental Health'}
                  {(activeSection === 'attendance' || activeSection === 'leaves') && 'Attendance & Leaves'}
                  {activeSection === 'syllabus' && 'Syllabus Management'}
                  {activeSection === 'create-test' && 'Create New Test'}
                  {activeSection === 'students' && 'Student Directory'}
                  {activeSection === 'attendance' && false /* now part of merged "Attendance & Leaves" header */}
                  {activeSection === 'online-classes' && 'Online Classes'}
                  {activeSection === 'mock-exams' && 'Class Exams'}
                  {activeSection === 'support' && 'Support'}
                </h2>
                <p className="text-gray-500 text-sm hidden sm:block">Welcome, {user?.name || 'Teacher'}!</p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <select 
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="border rounded-lg px-2 md:px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="class-filter-select"
              >
                {teacherClasses.length > 1 && <option value="all">All Classes</option>}
                {teacherClasses.map(cl => (
                  <option key={cl} value={cl}>Class {cl}</option>
                ))}
              </select>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="border rounded-lg px-2 md:px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="section-filter-select"
              >
                <option value="all">All Sections</option>
                {['A','B','C','D','E'].map(sec => (
                  <option key={sec} value={sec}>Section {sec}</option>
                ))}
              </select>
              <div className="text-right hidden md:block">
                <p className="text-xs text-gray-400">{format(new Date(), 'EEEE, dd MMM yyyy')}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Quick Stats Bar */}
        {activeSection === 'dashboard' && (
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-3 md:px-6 py-4">
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-4">
              <button 
                onClick={() => setActiveSection('students')}
                className="bg-white/10 hover:bg-white/20 rounded-lg p-4 text-white text-center transition-colors"
              >
                <p className="text-3xl font-bold">{filteredStudents.length}</p>
                <p className="text-sm opacity-80">Students</p>
              </button>
              <button 
                onClick={() => { setActiveSection('results'); setActiveSubSection('pending'); toggleMenu('results'); }}
                className="bg-white/10 hover:bg-white/20 rounded-lg p-4 text-white text-center transition-colors"
              >
                <p className="text-3xl font-bold">{results.filter(r => r.has_descriptive && !r.approved).length}</p>
                <p className="text-sm opacity-80">Pending Results</p>
              </button>
              <button 
                onClick={() => setActiveSection('leaves')}
                className="bg-white/10 hover:bg-white/20 rounded-lg p-4 text-white text-center transition-colors"
              >
                <p className="text-3xl font-bold">{pendingLeaves}</p>
                <p className="text-sm opacity-80">Pending Leaves</p>
              </button>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="p-3 md:p-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {/* Dashboard View */}
              {activeSection === 'dashboard' && (
                <div className="space-y-6">
                  {/* Holiday Calendar widget */}
                  <HolidayCalendar role="teacher" />

                  {/* Weekly Analytics + Section Leaderboard */}
                  <TeacherWeeklyAnalytics results={filteredResults} students={filteredStudents} allowedSubjects={teacherSubjects} />

                  <TeacherSectionLeaderboard students={filteredStudents} results={filteredResults} />

                  {/* Inactive Students Alert (no activity in 3+ days) */}
                  {inactiveStudents.total > 0 && (
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border-2 border-red-200" data-testid="inactive-students-card">
                      <div className="px-6 py-4 border-b bg-red-50 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="text-red-500 shrink-0" size={22} />
                          <h3 className="font-semibold text-red-700">
                            Students Inactive ({inactiveStudents.total})
                          </h3>
                        </div>
                        <span className="text-xs text-red-600">No test attempt in 3+ days</span>
                      </div>
                      <div className="p-4 max-h-72 overflow-y-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {inactiveStudents.inactive_students.slice(0, 30).map((s) => (
                            <div key={s.student_id} className="flex items-center justify-between p-3 bg-red-50/50 border border-red-100 rounded-lg" data-testid={`inactive-student-${s.student_id}`}>
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-gray-800 truncate">{s.name}</p>
                                <p className="text-xs text-gray-500">Class {s.class_level} · Roll {s.roll_number}</p>
                              </div>
                              <div className="text-right shrink-0 ml-2">
                                <p className="text-xs font-semibold text-red-600">
                                  {s.last_attempt_at
                                    ? `${s.days_inactive}d ago`
                                    : 'Never attempted'}
                                </p>
                                {s.last_attempt_at && (
                                  <p className="text-[10px] text-gray-400">
                                    {format(new Date(s.last_attempt_at), 'dd MMM')}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {inactiveStudents.total > 30 && (
                          <p className="text-xs text-gray-500 text-center mt-3">
                            … and {inactiveStudents.total - 30} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pending Leaves */}
                  {pendingLeaves > 0 && (
                    <div className="bg-white rounded-xl shadow-sm">
                      <div className="px-6 py-4 border-b">
                        <h3 className="font-semibold text-gray-800">Pending Leave Requests ({pendingLeaves})</h3>
                      </div>
                      <div className="p-4">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Student</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Dates</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Reason</th>
                                <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {leaves.filter(l => l.status === 'pending').slice(0, 3).map((leave, index) => (
                                <tr key={index}>
                                  <td className="px-4 py-3 font-medium">{leave.student_name}</td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    {leave.start_date ? format(new Date(leave.start_date), 'dd MMM') : leave.leave_date ? format(new Date(leave.leave_date), 'dd MMM') : '-'} 
                                    {leave.end_date ? ` - ${format(new Date(leave.end_date), 'dd MMM')}` : ''}
                                  </td>
                                  <td className="px-4 py-3 text-sm">{leave.reason}</td>
                                  <td className="px-4 py-3 text-center">
                                    <div className="flex justify-center gap-2">
                                      <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => approveLeave(leave.id)}>
                                        <Check size={16} />
                                      </Button>
                                      <Button size="sm" variant="outline" className="text-red-500" onClick={() => rejectLeave(leave.id)}>
                                        <X size={16} />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Results View */}
              {activeSection === 'results' && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-white font-semibold text-lg">
                        {activeSubSection === 'pending' ? 'Descriptive Answers — Needs My Review' : 'Auto-graded Test Results'}
                      </h3>
                      <p className="text-blue-100 text-xs mt-0.5">
                        {activeSubSection === 'pending'
                          ? 'Long-answer questions auto-scored by AI. Review and release scores to students.'
                          : 'Objective tests (MCQ, true/false, fill-blanks) — scores already released to students.'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
                        <label className="text-xs font-semibold uppercase text-blue-100">Class</label>
                        <select
                          value={resultsClassFilter}
                          onChange={(e) => setResultsClassFilter(e.target.value)}
                          className="bg-white text-gray-800 text-sm rounded-md px-2 py-1 border-0"
                          data-testid="results-class-filter"
                        >
                          <option value="all">All</option>
                          {['6', '7', '8', '9', '10'].map(c => <option key={c} value={c}>Class {c}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
                        <label className="text-xs font-semibold uppercase text-blue-100">Subject</label>
                        <select
                          value={resultsSubjectFilter}
                          onChange={(e) => setResultsSubjectFilter(e.target.value)}
                          className="bg-white text-gray-800 text-sm rounded-md px-2 py-1 border-0"
                          data-testid="results-subject-filter"
                        >
                          <option value="all">All</option>
                          {['Mathematics', 'Science', 'English', 'Social Studies'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
                        <label className="text-xs font-semibold uppercase text-blue-100">Exam Type</label>
                        <select
                          value={examTypeFilter}
                          onChange={(e) => setExamTypeFilter(e.target.value)}
                          className="bg-white text-gray-800 text-sm rounded-md px-2 py-1 border-0"
                          data-testid="results-exam-type-filter"
                        >
                          <option value="all">All</option>
                          <option value="topic_wise">Topic-wise</option>
                          <option value="unit">Unit Test</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="half_yearly">Half-Yearly</option>
                          <option value="annual">Finals</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-blue-900 text-white text-sm">
                        <tr>
                          <th className="px-4 py-3 text-left">Student</th>
                          <th className="px-4 py-3 text-left">Test</th>
                          <th className="px-4 py-3 text-center">Class</th>
                          <th className="px-4 py-3 text-center">Score</th>
                          {activeSubSection === 'pending' && (
                            <th className="px-4 py-3 text-center">Status</th>
                          )}
                          <th className="px-4 py-3 text-center">Date</th>
                          <th className="px-4 py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(activeSubSection === 'pending' 
                          ? dedupedResults.filter(r => r.has_descriptive && !r.approved)
                          : dedupedResults
                        ).map((result, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 font-medium">{result.student_name || 'Student'}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                  <span>{result.test_title || 'Test'}</span>
                                  {result._attemptCount > 1 && (
                                    <span className="text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded" title={`Student attempted this test ${result._attemptCount} times — showing latest`}>
                                      {result._attemptCount} attempts
                                    </span>
                                  )}
                                </div>
                                {result.exam_type && (
                                  <span className="text-xs text-gray-500 mt-0.5">
                                    {result.exam_type === 'topic_wise' ? 'Topic-wise · latest attempt' :
                                     result.exam_type === 'half_yearly' ? 'Half-Yearly' :
                                     result.exam_type === 'annual' ? 'Finals' :
                                     result.exam_type === 'unit' ? 'Unit Test' :
                                     result.exam_type === 'quarterly' ? 'Quarterly' : result.exam_type}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge variant="outline">Class {result.class_level || '6'}</Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(result.score)}`}>
                                {result.score}%
                              </span>
                            </td>
                            {activeSubSection === 'pending' && (
                              <td className="px-4 py-3 text-center">
                                {result.approved ? (
                                  <Badge className="bg-green-100 text-green-700">Reviewed</Badge>
                                ) : (
                                  <Badge className="bg-yellow-100 text-yellow-700">Needs Review</Badge>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-3 text-center text-sm text-gray-500">
                              {result.completed_at ? format(new Date(result.completed_at), 'dd MMM yyyy') : '-'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex justify-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => loadResultDetails(result.id)}>
                                  <Eye size={16} className="mr-1" />
                                  View
                                </Button>
                                {activeSubSection === 'pending' && !result.approved && (
                                  <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => approveResult(result.id)}>
                                    Approve
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {activeSubSection === 'pending' &&
                      filteredResults.filter(r => r.has_descriptive && !r.approved).length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                          <p className="font-medium">No descriptive answers waiting for review</p>
                          <p className="text-sm mt-1">Long-answer questions appear here once students submit them.</p>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Mental Health View */}
              {activeSection === 'mental-health' && (
                <div className="space-y-6">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-pink-500 to-pink-600 text-white rounded-xl p-5">
                      <Heart size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{mentalHealthOverview?.today_checkins || 0}</p>
                      <p className="text-sm opacity-80">Today's Check-ins</p>
                    </div>
                    <div className={`${talkRequests > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'} text-white rounded-xl p-5`}>
                      <MessageCircle size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{talkRequests}</p>
                      <p className="text-sm opacity-80">Talk Requests</p>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-5">
                      <AlertTriangle size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{mentalHealthOverview?.concerning_count || 0}</p>
                      <p className="text-sm opacity-80">Students to Watch</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-5">
                      <Smile size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{mentalHealthOverview?.mood_distribution?.happy || 0}</p>
                      <p className="text-sm opacity-80">Happy Today</p>
                    </div>
                  </div>

                  {/* Mood Distribution */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b">
                      <h3 className="font-semibold text-gray-800">Today's Mood Distribution</h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-5 gap-4">
                        {[
                          { mood: 'happy', color: 'bg-green-100 text-green-700 border-green-200', icon: Smile },
                          { mood: 'okay', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Meh },
                          { mood: 'sad', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Frown },
                          { mood: 'stressed', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle },
                          { mood: 'anxious', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle }
                        ].map(({ mood, color, icon: Icon }) => (
                          <div key={mood} className={`${color} border-2 rounded-xl p-4 text-center`}>
                            <Icon className="mx-auto mb-2" size={32} />
                            <p className="text-2xl font-bold">
                              {mentalHealthOverview?.mood_distribution?.[mood] || 0}
                            </p>
                            <p className="text-sm capitalize">{mood}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Alerts */}
                  {mentalHealthAlerts?.alerts?.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border-2 border-red-200">
                      <div className="px-6 py-4 border-b bg-red-50">
                        <h3 className="font-semibold text-red-700 flex items-center gap-2">
                          <AlertTriangle size={20} />
                          Students Needing Attention ({mentalHealthAlerts.total})
                        </h3>
                      </div>
                      <div className="p-4">
                        {mentalHealthAlerts.alerts.map((alert, index) => (
                          <div key={index} className={`mb-3 last:mb-0 border rounded-lg p-4 ${
                            alert.priority === 'high' ? 'bg-red-50 border-red-300' : 'bg-orange-50 border-orange-200'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {alert.type === 'talk_request' ? (
                                  <MessageCircle className="text-red-500" size={24} />
                                ) : (
                                  <AlertTriangle className="text-orange-500" size={24} />
                                )}
                                <div>
                                  <p className="font-medium">{alert.student_name} (Class {alert.class_level})</p>
                                  <p className="text-sm text-gray-600">{alert.message}</p>
                                  {alert.note && <p className="text-sm italic text-gray-500 mt-1">"{alert.note}"</p>}
                                </div>
                              </div>
                              <Button size="sm" variant="outline" onClick={() => loadStudentMHDetail(alert.student_id)}>
                                <Eye size={16} className="mr-1" />
                                View History
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Students View */}
              {activeSection === 'students' && (
                <div className="space-y-6">
                  {/* Read-only note: only HT can add students */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800" data-testid="teacher-students-readonly-note">
                    You can view and review students from your assigned sections here. To add or remove students, please ask your Head Teacher.
                  </div>

                  {/* Student List */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
                      <h3 className="text-white font-semibold text-lg">Student Directory ({filteredStudents.length} students)</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-blue-900 text-white text-sm">
                          <tr>
                            <th className="px-4 py-3 text-left">Name</th>
                            <th className="px-4 py-3 text-center">Roll Number</th>
                            <th className="px-4 py-3 text-center">Class</th>
                            <th className="px-4 py-3 text-center">Section</th>
                            <th className="px-4 py-3 text-center">Tests Taken</th>
                            <th className="px-4 py-3 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {filteredStudents.map((student, index) => {
                            const studentResults = results.filter(r => r.student_id === student.id);
                            return (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-4 py-3 font-medium">{student.name}</td>
                                <td className="px-4 py-3 text-center">{student.roll_number}</td>
                                <td className="px-4 py-3 text-center">
                                  <Badge variant="outline">Class {student.class_level}</Badge>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{student.section || 'A'}</Badge>
                                </td>
                                <td className="px-4 py-3 text-center">{studentResults.length}</td>
                                <td className="px-4 py-3 text-center">
                                  <div className="flex justify-center gap-2">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => viewStudentDetail(student)}
                                    >
                                      <Eye size={16} className="mr-1" />
                                      View
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Syllabus View */}
              {activeSection === 'syllabus' && (
                <div className="space-y-4">
                  {/* Class & Subject Selector */}
                  <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
                    <div className="flex gap-4 items-center flex-wrap">
                      <span className="font-medium text-gray-600">Class:</span>
                      {teacherClasses.map(cl => (
                        <button
                          key={cl}
                          onClick={() => setSelectedClass(cl)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            selectedClass === cl
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          data-testid={`syllabus-class-btn-${cl}`}
                        >
                          Class {cl}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-4 items-center flex-wrap">
                      <span className="font-medium text-gray-600">Subject:</span>
                    {teacherSubjects.map(subject => (
                      <button
                        key={subject}
                        onClick={() => setSelectedSubject(subject)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          selectedSubject === subject
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {subject}
                      </button>
                    ))}
                    {/* Generate All button */}
                    {syllabus?.chapters?.some(ch => {
                      const hasCovered = ch.topics?.some(t => t.status === 'covered');
                      return hasCovered && !ch.questions_generated && ch.generation_status !== 'generating';
                    }) && (
                      <Button
                        size="sm"
                        className="ml-auto bg-green-600 hover:bg-green-700 text-white"
                        data-testid="generate-all-questions-btn"
                        onClick={async () => {
                          const pending = syllabus.chapters.filter(ch => {
                            const hasCovered = ch.topics?.some(t => t.status === 'covered');
                            return hasCovered && !ch.questions_generated && ch.generation_status !== 'generating';
                          });
                          toast.success(`Starting generation for ${pending.length} chapters...`);
                          for (const ch of pending) {
                            try {
                              await axios.post(`${API}/skill-assessment/generate-questions/${ch.id}`);
                            } catch (e) {}
                            await new Promise(r => setTimeout(r, 500));
                          }
                          loadSyllabus();
                        }}
                      >
                        <Sparkles size={14} className="mr-1" />
                        Generate All ({syllabus?.chapters?.filter(ch => {
                          const hasCovered = ch.topics?.some(t => t.status === 'covered');
                          return hasCovered && !ch.questions_generated && ch.generation_status !== 'generating';
                        }).length})
                      </Button>
                    )}
                    </div>
                  </div>

                  {/* Current Selection Info */}
                  {syllabus && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-700 font-medium" data-testid="syllabus-current-info">
                      Showing: Class {syllabus.class_level} - {syllabus.subject} ({syllabus.board || 'SCERT'}) &bull; {syllabus.chapters?.length || 0} Chapters
                    </div>
                  )}

                  {/* Chapters */}
                  {syllabus?.chapters?.map((chapter, chIndex) => {
                    const coveredTopicsCount = chapter.topics?.filter(t => t.status === 'covered').length || 0;
                    const hasCoveredTopics = coveredTopicsCount > 0;
                    
                    return (
                    <div key={chIndex} className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="text-white font-semibold">
                              Chapter {chapter.order}: {chapter.name}
                            </h3>
                            <p className="text-blue-200 text-sm">{coveredTopicsCount} of {chapter.topics?.length || 0} topics covered</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-white/20 text-white">{chapter.term}</Badge>
                            {hasCoveredTopics && (
                              chapter.questions_generated ? (
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-white/20 text-green-200 border border-green-300" data-testid={`chapter-ready-${chapter.id}`}>
                                    <Check size={14} className="mr-1" />
                                    {chapter.question_count} Questions Ready
                                  </Badge>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-xs"
                                    data-testid={`preview-questions-btn-${chapter.id}`}
                                    onClick={async () => {
                                      try {
                                        const res = await axios.get(`${API}/teacher/questions/preview/${chapter.id}`);
                                        setQuestionPreview(res.data);
                                        setShowQuestionPreview(true);
                                      } catch (error) {
                                        toast.error(error.response?.data?.detail || 'Failed to load questions');
                                      }
                                    }}
                                  >
                                    <Eye size={14} className="mr-1" />
                                    Preview
                                  </Button>
                                </div>
                              ) : chapter.generation_status === 'generating' ? (
                                <Badge className="bg-yellow-500/20 text-yellow-200 border border-yellow-300 animate-pulse" data-testid={`chapter-generating-${chapter.id}`}>
                                  <Loader2 size={14} className="mr-1 animate-spin" />
                                  Generating...
                                </Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                  data-testid={`generate-questions-btn-${chapter.id}`}
                                  onClick={async () => {
                                    try {
                                      const res = await axios.post(`${API}/skill-assessment/generate-questions/${chapter.id}`);
                                      if (res.data.status === 'ready') {
                                        toast.info(`Questions already exist for ${chapter.name}`);
                                        loadSyllabus();
                                        return;
                                      }
                                      toast.success(`Generating questions for ${chapter.name}... This may take a minute.`);
                                      // Start polling
                                      const pollInterval = setInterval(async () => {
                                        try {
                                          const statusRes = await axios.get(`${API}/skill-assessment/generate-questions/status/${chapter.id}`);
                                          if (statusRes.data.status === 'ready') {
                                            clearInterval(pollInterval);
                                            toast.success(`${statusRes.data.total_questions} questions ready for ${chapter.name}!`);
                                            loadSyllabus();
                                          } else if (statusRes.data.status === 'failed') {
                                            clearInterval(pollInterval);
                                            toast.error(`Question generation failed for ${chapter.name}`);
                                            loadSyllabus();
                                          }
                                        } catch (err) {
                                          clearInterval(pollInterval);
                                        }
                                      }, 3000);
                                      loadSyllabus();
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Failed to start question generation');
                                    }
                                  }}
                                >
                                  <Sparkles size={14} className="mr-1" />
                                  Generate Questions
                                </Button>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        {chapter.topics?.map((topic, tIndex) => (
                          <div key={tIndex} className="flex items-center justify-between py-3 border-b last:border-0">
                            <div>
                              <p className="font-medium text-gray-800">{topic.name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant={topic.status === 'covered' ? 'default' : 'outline'}
                                className={topic.status === 'covered' ? 'bg-green-500' : ''}
                                onClick={() => updateTopicStatus(topic.id, topic.status === 'covered' ? 'not_covered' : 'covered')}
                              >
                                {topic.status === 'covered' ? (
                                  <><CheckCircle size={16} className="mr-1" /> Covered</>
                                ) : (
                                  'Mark Covered'
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )})}
                </div>
              )}

              {/* Create Test View */}
              {activeSection === 'create-test' && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
                    <h3 className="text-white font-semibold text-lg">Create AI-Generated Test</h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <Label>Test Title</Label>
                        <Input
                          value={testForm.title}
                          onChange={(e) => setTestForm(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="e.g., Math Quarterly Test 1"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Class</Label>
                        <select
                          value={testForm.class_level}
                          onChange={(e) => setTestForm(prev => ({ ...prev, class_level: e.target.value, topics: [] }))}
                          className="w-full mt-1 border rounded-lg px-4 py-2"
                        >
                          {teacherClasses.map(cl => (
                            <option key={cl} value={cl}>Class {cl}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Subject</Label>
                        <select
                          value={testForm.subject}
                          onChange={(e) => setTestForm(prev => ({ ...prev, subject: e.target.value, topics: [] }))}
                          className="w-full mt-1 border rounded-lg px-4 py-2"
                        >
                          {teacherSubjects.map(subj => (
                            <option key={subj} value={subj}>{subj}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Term</Label>
                        <select
                          value={testForm.category}
                          onChange={(e) => setTestForm(prev => ({ ...prev, category: e.target.value, topics: [] }))}
                          className="w-full mt-1 border rounded-lg px-4 py-2"
                        >
                          <option value="Quarterly">Quarterly</option>
                          <option value="Half-yearly">Half-yearly</option>
                          <option value="Finals">Finals</option>
                        </select>
                      </div>
                      <div>
                        <Label>Number of Questions</Label>
                        <Input
                          type="number"
                          value={testForm.total_questions}
                          onChange={(e) => setTestForm(prev => ({ ...prev, total_questions: parseInt(e.target.value) }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Duration (minutes)</Label>
                        <Input
                          type="number"
                          value={testForm.duration_minutes}
                          onChange={(e) => setTestForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Start Date</Label>
                        <Input
                          type="datetime-local"
                          value={testForm.start_date}
                          onChange={(e) => setTestForm(prev => ({ ...prev, start_date: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input
                          type="datetime-local"
                          value={testForm.end_date}
                          onChange={(e) => setTestForm(prev => ({ ...prev, end_date: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Topic Selection */}
                    <div className="mt-6">
                      <Label>Select Topics (Covered topics for {testForm.category})</Label>
                      <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg p-4 space-y-2">
                        {coveredTopics.length > 0 ? coveredTopics.map((topic, index) => (
                          <label key={index} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={testForm.topics.includes(topic.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setTestForm(prev => ({ ...prev, topics: [...prev.topics, topic.id] }));
                                } else {
                                  setTestForm(prev => ({ ...prev, topics: prev.topics.filter(t => t !== topic.id) }));
                                }
                              }}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm">{topic.chapter_name} - {topic.name}</span>
                          </label>
                        )) : (
                          <p className="text-gray-500 text-center py-4">No covered topics found. Mark some topics as covered in Syllabus first.</p>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-2">{testForm.topics.length} topic(s) selected</p>
                    </div>

                    <div className="mt-6">
                      <Button 
                        onClick={createTest}
                        disabled={creatingTest || !testForm.title || testForm.topics.length === 0}
                        className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                      >
                        {creatingTest ? (
                          <><Loader2 className="animate-spin mr-2" /> Creating Test...</>
                        ) : (
                          <><Plus className="mr-2" /> Create Test with AI Questions</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Leaves View */}
              {(activeSection === 'attendance' || activeSection === 'leaves') && (
                <div className="bg-white rounded-xl shadow-sm p-2 mb-4 flex gap-2" data-testid="attendance-leaves-tabs">
                  <button
                    onClick={() => setActiveSection('attendance')}
                    className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${
                      activeSection === 'attendance'
                        ? 'bg-teal-600 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid="tab-attendance"
                  >
                    Attendance
                  </button>
                  <button
                    onClick={() => setActiveSection('leaves')}
                    className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                      activeSection === 'leaves'
                        ? 'bg-teal-600 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid="tab-leaves"
                  >
                    Leaves
                    {pendingLeaves > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${activeSection === 'leaves' ? 'bg-white text-teal-700' : 'bg-red-100 text-red-700'}`}>
                        {pendingLeaves}
                      </span>
                    )}
                  </button>
                </div>
              )}

              {activeSection === 'leaves' && (
                <div className="space-y-6">
                  {/* My Leave Applications */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gradient-to-r from-teal-600 to-teal-700 flex justify-between items-center">
                      <h3 className="text-white font-semibold text-lg">My Leave Applications</h3>
                      <Button
                        size="sm"
                        className="bg-white text-teal-700 hover:bg-teal-50"
                        onClick={() => setShowLeaveForm(!showLeaveForm)}
                        data-testid="apply-leave-btn"
                      >
                        {showLeaveForm ? 'Cancel' : '+ Apply for Leave'}
                      </Button>
                    </div>
                    
                    {showLeaveForm && (
                      <div className="p-4 bg-teal-50 border-b space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm font-medium text-gray-600">From Date *</label>
                            <input
                              type="date"
                              value={leaveForm.leave_date}
                              onChange={(e) => setLeaveForm({ ...leaveForm, leave_date: e.target.value })}
                              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                              data-testid="leave-from-date"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">To Date (optional)</label>
                            <input
                              type="date"
                              value={leaveForm.end_date}
                              onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                              data-testid="leave-to-date"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Leave Type *</label>
                            <select
                              value={leaveForm.leave_type}
                              onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })}
                              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm bg-white"
                              data-testid="leave-type-select"
                            >
                              <option value="">Select leave type…</option>
                              {LEAVE_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                          <div />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Additional Notes (optional)</label>
                          <textarea
                            value={leaveForm.reason}
                            onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                            placeholder="Add any context for the Head Teacher (e.g. hospital appointment, family function)…"
                            rows={2}
                            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                            data-testid="leave-reason"
                          />
                        </div>
                        <Button
                          onClick={submitLeave}
                          disabled={submittingLeave}
                          className="bg-teal-600 hover:bg-teal-700"
                          data-testid="submit-leave-btn"
                        >
                          {submittingLeave ? 'Submitting...' : 'Submit Leave'}
                        </Button>
                      </div>
                    )}
                    
                    {myLeaves.length === 0 ? (
                      <div className="p-6 text-center text-gray-400">No leave applications yet</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 text-sm text-gray-600">
                            <tr>
                              <th className="px-4 py-3 text-left">Dates</th>
                              <th className="px-4 py-3 text-left">Type</th>
                              <th className="px-4 py-3 text-left">Notes</th>
                              <th className="px-4 py-3 text-center">Status</th>
                              <th className="px-4 py-3 text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {myLeaves.map((leave, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-4 py-3 text-sm">
                                  {leave.leave_date ? new Date(leave.leave_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '-'}
                                  {leave.end_date ? ` - ${new Date(leave.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                                </td>
                                <td className="px-4 py-3">
                                  {leave.leave_type ? (
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded border ${LEAVE_TYPE_COLORS[leave.leave_type] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
                                      {LEAVE_TYPE_LABELS[leave.leave_type] || leave.leave_type}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700">{leave.reason}</td>
                                <td className="px-4 py-3 text-center">
                                  {leave.status === 'approved' && <Badge className="bg-green-100 text-green-700">Approved</Badge>}
                                  {leave.status === 'rejected' && <Badge className="bg-red-100 text-red-700">Rejected</Badge>}
                                  {leave.status === 'pending' && <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {leave.status === 'pending' && (
                                    <Button size="sm" variant="outline" className="text-red-500 text-xs" onClick={() => cancelMyLeave(leave.id)} data-testid={`cancel-leave-${idx}`}>
                                      Cancel
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Student Leave Requests */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
                      <h3 className="text-white font-semibold text-lg">Student Leave Requests</h3>
                    </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-blue-900 text-white text-sm">
                        <tr>
                          <th className="px-4 py-3 text-left">Student</th>
                          <th className="px-4 py-3 text-center">Class</th>
                          <th className="px-4 py-3 text-left">Dates</th>
                          <th className="px-4 py-3 text-left">Reason</th>
                          <th className="px-4 py-3 text-center">Status</th>
                          <th className="px-4 py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {leaves.map((leave, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 font-medium">{leave.student_name}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge variant="outline">Class {leave.class_level || '6'}</Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {leave.start_date ? format(new Date(leave.start_date), 'dd MMM') : leave.leave_date ? format(new Date(leave.leave_date), 'dd MMM') : '-'}
                              {leave.end_date ? ` - ${format(new Date(leave.end_date), 'dd MMM yyyy')}` : ''}
                            </td>
                            <td className="px-4 py-3">{leave.reason}</td>
                            <td className="px-4 py-3 text-center">
                              {leave.status === 'approved' && <Badge className="bg-green-100 text-green-700">Approved</Badge>}
                              {leave.status === 'rejected' && <Badge className="bg-red-100 text-red-700">Rejected</Badge>}
                              {leave.status === 'pending' && <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {leave.status === 'pending' && (
                                <div className="flex justify-center gap-2">
                                  <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => approveLeave(leave.id)}>
                                    <Check size={16} />
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-500" onClick={() => rejectLeave(leave.id)}>
                                    <X size={16} />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </div>
                </div>
              )}

              {/* Attendance Section */}
              {/* Mock Exams Section */}
              {activeSection === 'mock-exams' && (
                <TeacherMockExams teacherInfo={{ subjects: teacherSubjects, class_levels: teacherClasses }} />
              )}

              {/* AI Practice Suggestions Section */}
              {activeSection === 'practice-suggestions' && (
                <TeacherPracticeSuggestions onCountChange={setPracticeSuggestionsCount} />
              )}

              {/* Class Improvement Section */}
              {activeSection === 'class-improvement' && (
                <ClassImprovementDashboard />
              )}

              {/* Skill Assessment Review Section */}
              {activeSection === 'skill-review' && (
                <TeacherSkillAssessmentReview onBack={() => setActiveSection('dashboard')} />
              )}

              {/* Online Classes Section */}
              {activeSection === 'online-classes' && (
                <div className="space-y-4">
                  {/* Attendance Modal */}
                  {showAttendance && classAttendance && (
                    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowAttendance(false)}>
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5">
                          <div className="flex justify-between items-start">
                            <div>
                              <h2 className="text-lg font-bold">{classAttendance.title} - Attendance</h2>
                              <p className="text-indigo-100 text-sm mt-1">{classAttendance.attended_count}/{classAttendance.total_students} students ({classAttendance.attendance_percentage}%)</p>
                            </div>
                            <button onClick={() => setShowAttendance(false)} className="text-white/80 hover:text-white text-2xl">&times;</button>
                          </div>
                          <div className="flex gap-4 mt-3">
                            <div className="bg-green-500/30 px-3 py-1 rounded-lg text-sm">Present: {classAttendance.attended_count}</div>
                            <div className="bg-red-500/30 px-3 py-1 rounded-lg text-sm">Absent: {classAttendance.missed_count}</div>
                          </div>
                        </div>
                        <div className="overflow-y-auto" style={{maxHeight: 'calc(80vh - 140px)'}}>
                          {classAttendance.attended.length > 0 && (
                            <div className="p-4">
                              <h4 className="text-sm font-semibold text-green-700 mb-2">Present ({classAttendance.attended.length})</h4>
                              <div className="space-y-1">
                                {classAttendance.attended.map((s, i) => (
                                  <div key={i} className="flex justify-between items-center px-3 py-2 bg-green-50 rounded-lg text-sm">
                                    <span className="font-medium">{s.student_name} {s.roll_number && `(${s.roll_number})`}</span>
                                    <span className="text-green-600 text-xs">{s.joined_at ? new Date(s.joined_at).toLocaleTimeString('en-IN', {hour:'2-digit', minute:'2-digit'}) : ''}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {classAttendance.missed.length > 0 && (
                            <div className="p-4 pt-0">
                              <h4 className="text-sm font-semibold text-red-700 mb-2">Absent ({classAttendance.missed.length})</h4>
                              <div className="space-y-1">
                                {classAttendance.missed.map((s, i) => (
                                  <div key={i} className="flex justify-between items-center px-3 py-2 bg-red-50 rounded-lg text-sm">
                                    <span className="font-medium">{s.student_name} {s.roll_number && `(${s.roll_number})`}</span>
                                    <span className="text-red-400 text-xs">Absent</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-4 md:px-6 py-4 border-b bg-gradient-to-r from-indigo-600 to-purple-600 flex justify-between items-center">
                      <h3 className="text-white font-semibold text-lg">Scheduled Classes</h3>
                      <Button
                        size="sm"
                        className="bg-white text-indigo-700 hover:bg-indigo-50"
                        onClick={() => setShowClassForm(!showClassForm)}
                        data-testid="schedule-class-btn"
                      >
                        {showClassForm ? 'Cancel' : '+ Schedule Class'}
                      </Button>
                    </div>
                    
                    {showClassForm && (
                      <div className="p-4 bg-indigo-50 border-b space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Class Title *</label>
                            <input type="text" value={classForm.title} onChange={(e) => setClassForm({ ...classForm, title: e.target.value })} placeholder="e.g. Algebra Revision" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="class-title-input" />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Subject *</label>
                            <select value={classForm.subject} onChange={(e) => setClassForm({ ...classForm, subject: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="class-subject-select">
                              <option value="">Select Subject</option>
                              {(user?.subjects || ['Mathematics', 'Science', 'English', 'Social Studies']).map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Class Level *</label>
                            <select value={classForm.class_level} onChange={(e) => setClassForm({ ...classForm, class_level: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="class-level-select">
                              <option value="">Select Class</option>
                              {teacherClasses.map(cl => <option key={cl} value={cl}>Class {cl}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Date & Time *</label>
                            <input type="datetime-local" value={classForm.scheduled_at} onChange={(e) => setClassForm({ ...classForm, scheduled_at: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="class-datetime-input" />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Duration (min)</label>
                            <input type="number" value={classForm.duration_minutes} onChange={(e) => setClassForm({ ...classForm, duration_minutes: parseInt(e.target.value) || 45 })} className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Google Meet Link *</label>
                          <input type="url" value={classForm.meet_link} onChange={(e) => setClassForm({ ...classForm, meet_link: e.target.value })} placeholder="https://meet.google.com/xxx-xxxx-xxx" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="meet-link-input" />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Topic / Description</label>
                          <input type="text" value={classForm.topic || ''} onChange={(e) => setClassForm({ ...classForm, topic: e.target.value })} placeholder="e.g. Chapter 3 - Linear Equations" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" />
                        </div>
                        <Button onClick={scheduleClass} disabled={submittingClass} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-class-btn">
                          {submittingClass ? 'Scheduling...' : 'Schedule Class'}
                        </Button>
                      </div>
                    )}
                    
                    {onlineClasses.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">
                        <Sparkles className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No classes scheduled yet. Click "Schedule Class" to create one.</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {onlineClasses.map((oc, idx) => {
                          const isUpcoming = new Date(oc.scheduled_at) > new Date();
                          const isPast = !isUpcoming;
                          return (
                            <div key={idx} className={`p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${isPast ? 'bg-gray-50' : ''}`} data-testid={`online-class-${idx}`}>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-semibold text-gray-800">{oc.title}</h4>
                                  <Badge className={isUpcoming ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}>
                                    {oc.status === 'cancelled' ? 'Cancelled' : isUpcoming ? 'Upcoming' : 'Completed'}
                                  </Badge>
                                  <Badge variant="outline">{oc.subject}</Badge>
                                  <Badge variant="outline">Class {oc.class_level}</Badge>
                                </div>
                                {oc.topic && <p className="text-sm text-gray-500 mt-1">{oc.topic}</p>}
                                <p className="text-sm text-gray-500 mt-1">
                                  {new Date(oc.scheduled_at).toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  {' '}({oc.duration_minutes} min) | {oc.attendees?.length || 0} students joined
                                </p>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-200" onClick={() => viewAttendance(oc.id)} data-testid={`view-attendance-${idx}`}>
                                  {oc.attendees?.length || 0} Attended
                                </Button>
                                {isUpcoming && (
                                  <a href={oc.meet_link} target="_blank" rel="noopener noreferrer">
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700">Start Class</Button>
                                  </a>
                                )}
                                <Button size="sm" variant="outline" className="text-red-500 border-red-200" onClick={() => deleteOnlineClass(oc.id)} data-testid={`delete-class-${idx}`}>
                                  Delete
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeSection === 'attendance' && (
                <AttendanceSection 
                  selectedClass={selectedClass}
                  students={students}
                  schoolId={user?.school_id}
                />
              )}



              {/* Support - Coming Soon */}
              {activeSection === 'support' && (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                  <Trophy className="mx-auto text-gray-300 mb-4" size={64} />
                  <h3 className="text-xl font-semibold text-gray-600">Coming Soon!</h3>
                  <p className="text-gray-400 mt-2">This feature is under development</p>
                </div>
              )}

              {/* Skill Assessment Analytics */}
              {activeSection === 'skill-assessment' && (
                <TeacherSkillAnalytics 
                  onBack={() => setActiveSection('dashboard')}
                  classLevel={selectedClass || "6"}
                  allowedSubjects={teacherSubjects}
                />
              )}
            </>
          )}
        </div>
      </main>

      {/* Result Details Modal */}
      {selectedResult && resultDetails && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <FileText size={24} />
                  Test Result Details
                </h2>
                <p className="text-blue-200 text-sm">{resultDetails.test?.title || 'Test'}</p>
              </div>
              <button
                onClick={() => { setSelectedResult(null); setResultDetails(null); }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Student Info */}
              <div className="bg-blue-50 rounded-xl p-4 mb-6 flex items-center gap-4">
                <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xl font-bold">{resultDetails.student?.name?.charAt(0) || 'S'}</span>
                </div>
                <div>
                  <p className="font-semibold text-lg">{resultDetails.student?.name || 'Student'}</p>
                  <p className="text-gray-500">Roll: {resultDetails.student?.roll_number || 'N/A'} | Class: {resultDetails.student?.class_level || 'N/A'}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className={`text-3xl font-bold ${
                    resultDetails.score >= 80 ? 'text-green-600' : 
                    resultDetails.score >= 50 ? 'text-orange-600' : 'text-red-600'
                  }`}>
                    {resultDetails.score || 0}%
                  </p>
                  <Badge className={
                    resultDetails.score >= 80 ? 'bg-green-100 text-green-700' : 
                    resultDetails.score >= 50 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                  }>
                    {resultDetails.score >= 80 ? 'Excellent' : resultDetails.score >= 50 ? 'Average' : 'Needs Improvement'}
                  </Badge>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <CheckCircle className="mx-auto text-green-500 mb-2" size={28} />
                  <p className="text-2xl font-bold text-green-600">{resultDetails.correct_answers || 0}</p>
                  <p className="text-sm text-gray-500">Correct</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <X className="mx-auto text-red-500 mb-2" size={28} />
                  <p className="text-2xl font-bold text-red-600">{resultDetails.wrong_answers || 0}</p>
                  <p className="text-sm text-gray-500">Wrong</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <FileText className="mx-auto text-gray-500 mb-2" size={28} />
                  <p className="text-2xl font-bold text-gray-600">{resultDetails.total_questions || 0}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <Award className="mx-auto text-purple-500 mb-2" size={28} />
                  <p className="text-2xl font-bold text-purple-600">{resultDetails.test?.subject || 'N/A'}</p>
                  <p className="text-sm text-gray-500">Subject</p>
                </div>
              </div>

              {/* View toggle (Insights tab hidden for auto-graded Topic Tests) */}
              {resultDetails.exam_type !== 'topic_wise' ? (
                <div className="mb-4 flex gap-2 bg-gray-100 p-1 rounded-lg w-fit">
                  <Button
                    size="sm"
                    variant={resultsModalView === 'insights' ? 'default' : 'ghost'}
                    onClick={() => setResultsModalView('insights')}
                    data-testid="results-tab-insights"
                  >
                    ✨ Insights & Action Plan
                  </Button>
                  <Button
                    size="sm"
                    variant={resultsModalView === 'details' ? 'default' : 'ghost'}
                    onClick={() => setResultsModalView('details')}
                    data-testid="results-tab-details"
                  >
                    Question Details
                  </Button>
                </div>
              ) : (
                <div className="mb-4 text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                  <Sparkles size={14} className="text-blue-500" />
                  Topic Test — auto-graded. Use <strong>Student Review &amp; AI Plan</strong> from the sidebar for AI insights across all of this student's work.
                </div>
              )}

              {/* Insights tab — skipped entirely for Topic Tests */}
              {resultDetails.exam_type !== 'topic_wise' && resultsModalView === 'insights' && resultDetails.attempt_id && (
                <StudentInsights
                  diagnosticUrl={`${API}/teacher/results/${resultDetails.attempt_id}/diagnostic`}
                  actionPlanUrl={`${API}/teacher/results/${resultDetails.attempt_id}/ai-action-plan`}
                  onShowDetails={() => setResultsModalView('details')}
                />
              )}

              {/* Question-wise Breakdown (always shown for Topic Tests; toggled for others) */}
              {(resultDetails.exam_type === 'topic_wise' || resultsModalView === 'details') && resultDetails.questions && resultDetails.questions.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-700 mb-3">Question-wise Breakdown</h3>
                  <div className="space-y-3 max-h-[450px] overflow-y-auto pr-1">
                    {resultDetails.questions.map((q, idx) => {
                      const isMCQ = (q.question_type || '').toLowerCase() === 'mcq' && q.options;
                      // Normalize options into { letter: text } pairs
                      let optionPairs = [];
                      if (q.options) {
                        if (Array.isArray(q.options)) {
                          const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
                          optionPairs = q.options.map((t, i) => [letters[i], t]);
                        } else if (typeof q.options === 'object') {
                          optionPairs = Object.entries(q.options);
                        }
                      }
                      const studentText = q.student_answer === 'Not answered' ? null : q.student_answer;
                      return (
                        <div key={idx} className={`p-3 rounded-lg border ${q.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <div className="flex items-start gap-2 mb-2">
                            <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${q.is_correct ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                              {q.question_number || idx + 1}
                            </span>
                            <p className="text-sm font-semibold text-gray-800 flex-1">{q.question_text}</p>
                            {q.is_correct ? (
                              <CheckCircle size={20} className="text-green-600 shrink-0" />
                            ) : (
                              <X size={20} className="text-red-600 shrink-0" />
                            )}
                          </div>

                          {/* MCQ: full options list */}
                          {isMCQ && optionPairs.length > 0 && (
                            <div className="space-y-1 mb-2 ml-9">
                              {optionPairs.map(([letter, text]) => {
                                const isRight = letter === q.correct_answer;
                                const isPick = letter === studentText;
                                const cls = isRight
                                  ? 'bg-green-100 border-green-300 text-green-900'
                                  : isPick
                                    ? 'bg-red-100 border-red-300 text-red-900'
                                    : 'bg-white border-gray-200 text-gray-700';
                                return (
                                  <div key={letter} className={`flex items-start gap-2 p-2 rounded border text-xs ${cls}`}>
                                    <span className="font-semibold">{letter}.</span>
                                    <span className="flex-1">{text}</span>
                                    {isRight && <span className="text-green-700 font-medium shrink-0">✓ Correct</span>}
                                    {isPick && !isRight && <span className="text-red-700 font-medium shrink-0">✗ Student picked</span>}
                                  </div>
                                );
                              })}
                              {!studentText && (
                                <p className="text-xs text-amber-700 italic">Student did not answer.</p>
                              )}
                            </div>
                          )}

                          {/* Non-MCQ: side-by-side answer vs model answer */}
                          {!isMCQ && (
                            <div className="space-y-2 ml-9">
                              <div className="bg-red-50 border border-red-200 rounded p-2">
                                <p className="text-xs font-semibold text-red-700 mb-1">Student's answer:</p>
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">{studentText || <em className="text-gray-400">Not answered</em>}</p>
                              </div>
                              <div className="bg-green-50 border border-green-200 rounded p-2">
                                <p className="text-xs font-semibold text-green-700 mb-1">Correct / Model answer:</p>
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">{q.correct_answer}</p>
                              </div>
                            </div>
                          )}

                          {/* Explanation */}
                          {q.explanation && (
                            <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2 ml-9">
                              <p className="text-xs font-semibold text-blue-700 mb-0.5">Why:</p>
                              <p className="text-xs text-blue-900">{q.explanation}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Buttons (Approve hidden for auto-graded Topic Tests) */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                {resultDetails.result_status !== 'approved' && resultDetails.exam_type !== 'topic_wise' && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => { approveResult(selectedResult); setSelectedResult(null); setResultDetails(null); }}
                  >
                    <Check size={16} className="mr-2" />
                    Approve Result
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setSelectedResult(null); setResultDetails(null); }}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Student Detail Modal */}
      {selectedStudentDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl flex justify-between items-center sticky top-0">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Users className="text-white" size={24} />
                </div>
                <div className="text-white">
                  <h2 className="text-xl font-bold">{selectedStudentDetail.name}</h2>
                  <p className="text-blue-100 text-sm">Class {selectedStudentDetail.class_level} • Roll: {selectedStudentDetail.roll_number}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/20"
                onClick={closeStudentDetail}
              >
                <X size={24} />
              </Button>
            </div>

            {/* Modal Content */}
            {loadingStudentDetail ? (
              <div className="p-12 text-center">
                <Loader2 className="mx-auto text-blue-500 mb-4 animate-spin" size={48} />
                <p className="text-gray-500">Loading student details...</p>
              </div>
            ) : studentDetailData && (
              <div className="p-6 space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <FileText className="mx-auto text-blue-600 mb-2" size={28} />
                    <p className="text-2xl font-bold text-blue-700">{studentDetailData.stats.totalTests}</p>
                    <p className="text-sm text-blue-600">Tests Taken</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <TrendingUp className="mx-auto text-green-600 mb-2" size={28} />
                    <p className="text-2xl font-bold text-green-700">{studentDetailData.stats.avgScore}%</p>
                    <p className="text-sm text-green-600">Avg Score</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4 text-center">
                    <CheckCircle className="mx-auto text-emerald-600 mb-2" size={28} />
                    <p className="text-2xl font-bold text-emerald-700">{studentDetailData.stats.passedTests}</p>
                    <p className="text-sm text-emerald-600">Tests Passed</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <X className="mx-auto text-red-600 mb-2" size={28} />
                    <p className="text-2xl font-bold text-red-700">{studentDetailData.stats.failedTests}</p>
                    <p className="text-sm text-red-600">Tests Failed</p>
                  </div>
                </div>

                {/* Subject-wise Performance */}
                {Object.keys(studentDetailData.subjectStats).length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <BarChart3 size={20} />
                      Subject-wise Performance
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {Object.entries(studentDetailData.subjectStats).map(([subject, data]) => {
                        const avgScore = Math.round(data.totalScore / data.count);
                        return (
                          <div key={subject} className="bg-white rounded-lg p-3 border">
                            <p className="font-medium text-gray-700">{subject}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-sm text-gray-500">{data.count} tests</span>
                              <Badge className={avgScore >= 75 ? 'bg-green-100 text-green-700' : avgScore >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                                {avgScore}% avg
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Test History */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText size={20} />
                    Test History
                  </h3>
                  {studentDetailData.results.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                      <FileText className="mx-auto mb-2 text-gray-300" size={40} />
                      <p>No tests taken yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-100 text-gray-600 text-sm">
                          <tr>
                            <th className="px-4 py-2 text-left">Test Name</th>
                            <th className="px-4 py-2 text-center">Subject</th>
                            <th className="px-4 py-2 text-center">Score</th>
                            <th className="px-4 py-2 text-center">Date</th>
                            <th className="px-4 py-2 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {studentDetailData.results.slice(0, 10).map((result, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium">{result.test_name || 'Test'}</td>
                              <td className="px-4 py-2 text-center">
                                <Badge variant="outline">{result.subject}</Badge>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <Badge className={result.score >= 75 ? 'bg-green-100 text-green-700' : result.score >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                                  {result.score}%
                                </Badge>
                              </td>
                              <td className="px-4 py-2 text-center text-sm text-gray-500">
                                {result.completed_at ? format(new Date(result.completed_at), 'dd MMM yyyy') : '-'}
                              </td>
                              <td className="px-4 py-2 text-center">
                                {result.result_status === 'approved' ? (
                                  <Badge className="bg-green-100 text-green-700">Approved</Badge>
                                ) : (
                                  <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {studentDetailData.results.length > 10 && (
                        <p className="text-sm text-gray-500 text-center mt-2">
                          Showing 10 of {studentDetailData.results.length} tests
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Skill Progress (if available) */}
                {studentDetailData.skillProgress && studentDetailData.skillProgress.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Brain size={20} />
                      Skill Progress
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {studentDetailData.skillProgress.slice(0, 8).map((skill, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                          <span className="text-sm text-gray-700">{skill.skill_name}</span>
                          <Badge className={
                            skill.proficiency === 'strong' ? 'bg-green-100 text-green-700' :
                            skill.proficiency === 'average' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {skill.proficiency === 'strong' ? '🟢 Strong' :
                             skill.proficiency === 'average' ? '🟡 Average' : '🔴 Weak'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={closeStudentDetail}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Attendance Section Component
const AttendanceSection = ({ selectedClass, students }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [viewMode, setViewMode] = useState('mark'); // 'mark' or 'summary'
  const [classFilter, setClassFilter] = useState(selectedClass && selectedClass !== 'all' ? String(selectedClass) : '6');
  // Date range for Summary view (defaults to current month)
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const todayStr = today.toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(todayStr);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const token = sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (viewMode === 'mark') {
      loadExistingAttendance();
    } else {
      loadSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, classFilter, viewMode, fromDate, toDate]);

  const loadExistingAttendance = async () => {
    setLoading(true);
    try {
      const [classRes, summaryRes] = await Promise.all([
        axios.get(`${API}/teacher/attendance/class`, {
          params: { class_level: classFilter, date: selectedDate },
          headers
        }),
        // Lifetime summary so we can show each student's overall % beside their name
        axios.get(`${API}/teacher/attendance/summary`, {
          params: { class_level: classFilter },
          headers
        }).catch(() => ({ data: [] })),
      ]);
      const records = {};
      (classRes.data.records || []).forEach(r => {
        records[r.student_id] = r.status;
      });
      setAttendanceRecords(records);
      setSummaryData(summaryRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/teacher/attendance/summary`, {
        params: { class_level: classFilter, from_date: fromDate || undefined, to_date: toDate || undefined },
        headers
      });
      setSummaryData(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const setAttendance = (studentId, status) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const markAllPresent = () => {
    const filtered = (students || []).filter(s => s.class_level === classFilter);
    const records = {};
    filtered.forEach(s => { records[s.id] = 'present'; });
    setAttendanceRecords(records);
  };

  const markAllAbsent = () => {
    const filtered = (students || []).filter(s => s.class_level === classFilter);
    const records = {};
    filtered.forEach(s => { records[s.id] = 'absent'; });
    setAttendanceRecords(records);
  };

  const saveAttendance = async () => {
    const records = Object.entries(attendanceRecords).map(([student_id, status]) => ({
      student_id,
      status
    }));
    if (records.length === 0) {
      toast.error('No attendance to save');
      return;
    }
    const presentNow = records.filter(r => r.status === 'present').length;
    const absentNow = records.filter(r => r.status === 'absent').length;
    setSaving(true);
    try {
      await axios.post(`${API}/teacher/attendance/mark`, {
        class_level: classFilter,
        date: selectedDate,
        records
      }, { headers });
      toast.success(`✓ Saved · ${presentNow} present, ${absentNow} absent (${records.length} students total)`);
      // Refresh attendance summary so % badges update immediately
      try {
        const res = await axios.get(`${API}/teacher/attendance/summary`, {
          params: { class_level: classFilter },
          headers,
        });
        setSummaryData(res.data || []);
      } catch { /* silent */ }
    } catch (err) {
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const filteredStudents = (students || []).filter(s => s.class_level === classFilter);
  const presentCount = Object.values(attendanceRecords).filter(s => s === 'present').length;
  const absentCount = Object.values(attendanceRecords).filter(s => s === 'absent').length;
  const summaryByStudent = summaryData.reduce((acc, s) => { acc[s.student_id] = s; return acc; }, {});
  const pctBadge = (p) => {
    if (p == null || p === 0) return 'bg-gray-100 text-gray-500';
    if (p >= 90) return 'bg-emerald-100 text-emerald-700';
    if (p >= 75) return 'bg-amber-100 text-amber-700';
    if (p >= 50) return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="space-y-6" data-testid="attendance-section">
      {/* Tab Bar */}
      <div className="flex gap-3 items-center">
        <Button
          variant={viewMode === 'mark' ? 'default' : 'outline'}
          onClick={() => setViewMode('mark')}
          className={viewMode === 'mark' ? 'bg-teal-600 hover:bg-teal-700' : ''}
          data-testid="mark-attendance-tab"
        >
          <CheckCircle className="w-4 h-4 mr-2" /> Mark Attendance
        </Button>
        <Button
          variant={viewMode === 'summary' ? 'default' : 'outline'}
          onClick={() => setViewMode('summary')}
          className={viewMode === 'summary' ? 'bg-teal-600 hover:bg-teal-700' : ''}
          data-testid="attendance-summary-tab"
        >
          <BarChart3 className="w-4 h-4 mr-2" /> Summary
        </Button>
        <div className="ml-auto flex gap-2 items-center">
          <span className="text-sm text-gray-500">Class:</span>
          <select
            value={classFilter}
            onChange={e => setClassFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
            data-testid="attendance-class-filter"
          >
            <option value="6">Class 6</option>
            <option value="7">Class 7</option>
            <option value="8">Class 8</option>
            <option value="9">Class 9</option>
          </select>
        </div>
      </div>

      {viewMode === 'mark' ? (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="mark-attendance-view">
          {/* Stat tiles — give teachers a clear "how many marked" view */}
          {(() => {
            const total = filteredStudents.length;
            const marked = presentCount + absentCount;
            const notMarked = Math.max(0, total - marked);
            return (
              <div className="grid grid-cols-4 gap-3 p-4 bg-gradient-to-r from-teal-50 to-emerald-50 border-b">
                <div className="bg-white rounded-lg p-3 shadow-sm border border-emerald-200">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Present</p>
                  <p className="text-2xl font-bold text-emerald-600 tabular-nums" data-testid="att-present-count">{presentCount}</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm border border-red-200">
                  <p className="text-[11px] uppercase tracking-wide text-red-700 font-semibold">Absent</p>
                  <p className="text-2xl font-bold text-red-600 tabular-nums" data-testid="att-absent-count">{absentCount}</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm border border-amber-200">
                  <p className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Not marked</p>
                  <p className="text-2xl font-bold text-amber-600 tabular-nums" data-testid="att-not-marked-count">{notMarked}</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Total · Class {classFilter}</p>
                  <p className="text-2xl font-bold text-gray-800 tabular-nums" data-testid="att-total-count">{total}</p>
                  {total > 0 && marked > 0 && (
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {Math.round((presentCount / total) * 100)}% present
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Date selector row */}
          <div className="p-4 border-b bg-white flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal-600" />
              <input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
                data-testid="attendance-date-input"
              />
              <span className="text-xs text-gray-500">{new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="px-5 py-3 border-b flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={markAllPresent} className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="mark-all-present-btn">
              <Check className="w-3 h-3 mr-1" /> Mark All Present
            </Button>
            <Button size="sm" variant="outline" onClick={markAllAbsent} className="text-xs border-red-300 text-red-700 hover:bg-red-50" data-testid="mark-all-absent-btn">
              Mark All Absent
            </Button>
            <Button
              size="sm"
              onClick={saveAttendance}
              disabled={saving || Object.keys(attendanceRecords).length === 0}
              className="bg-teal-600 hover:bg-teal-700 text-xs ml-auto"
              data-testid="save-attendance-btn"
            >
              {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving...</> : 'Save Attendance'}
            </Button>
          </div>

          {/* Student List */}
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" />
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No students in Class {classFilter}</div>
          ) : (
            <div className="divide-y">
              {filteredStudents.map((student, idx) => {
                const status = attendanceRecords[student.id] || 'unmarked';
                return (
                  <div
                    key={student.id}
                    className={`flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors ${
                      status === 'present' ? 'bg-emerald-50/50' : status === 'absent' ? 'bg-red-50/50' : ''
                    }`}
                    data-testid={`attendance-row-${student.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-400 w-6">{idx + 1}</span>
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-sm">
                        {student.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-sm flex items-center gap-2">
                          {student.name}
                          {summaryByStudent[student.id] && summaryByStudent[student.id].total_days > 0 && (
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${pctBadge(summaryByStudent[student.id].percentage)}`}
                              title={`Overall: ${summaryByStudent[student.id].present}/${summaryByStudent[student.id].total_days} days present`}
                              data-testid={`student-pct-${student.id}`}
                            >
                              {summaryByStudent[student.id].percentage}%
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">Roll: {student.roll_number || '-'}</p>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => setAttendance(student.id, 'present')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          status === 'present'
                            ? 'bg-emerald-500 text-white hover:bg-emerald-600 ring-2 ring-emerald-300'
                            : 'bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50'
                        }`}
                        data-testid={`mark-present-${student.id}`}
                        title="Mark Present"
                      >
                        <Check className="w-3.5 h-3.5 inline mr-1" /> Present
                      </button>
                      <button
                        onClick={() => setAttendance(student.id, 'absent')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          status === 'absent'
                            ? 'bg-red-500 text-white hover:bg-red-600 ring-2 ring-red-300'
                            : 'bg-white text-red-700 border border-red-300 hover:bg-red-50'
                        }`}
                        data-testid={`mark-absent-${student.id}`}
                        title="Mark Absent"
                      >
                        Absent
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Summary View */
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="attendance-summary-view">
          <div className="p-5 border-b bg-gradient-to-r from-teal-50 to-white flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-semibold text-gray-800">Attendance Summary - Class {classFilter}</h3>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-teal-600" />
              <span className="text-gray-500">From</span>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="border rounded-lg px-2 py-1.5 text-sm"
                data-testid="summary-from-date"
                max={toDate || undefined}
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                className="border rounded-lg px-2 py-1.5 text-sm"
                data-testid="summary-to-date"
                min={fromDate || undefined}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setFromDate(firstOfMonth); setToDate(todayStr); }}
                className="text-xs ml-1"
                data-testid="reset-date-range-btn"
              >
                This Month
              </Button>
            </div>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>
          ) : summaryData.filter(s => s.class_level === classFilter).length === 0 ? (
            <div className="p-8 text-center text-gray-400">No attendance data yet for Class {classFilter}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium text-gray-600">#</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-600">Student</th>
                    <th className="text-center px-5 py-3 font-medium text-gray-600">Total Days</th>
                    <th className="text-center px-5 py-3 font-medium text-emerald-600">Present</th>
                    <th className="text-center px-5 py-3 font-medium text-red-600">Absent</th>
                    <th className="text-center px-5 py-3 font-medium text-gray-600">Percentage</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {summaryData
                    .filter(s => s.class_level === classFilter)
                    .sort((a, b) => b.percentage - a.percentage)
                    .map((s, idx) => (
                    <tr key={s.student_id} className="hover:bg-gray-50" data-testid={`summary-row-${s.student_id}`}>
                      <td className="px-5 py-3 text-gray-400">{idx + 1}</td>
                      <td className="px-5 py-3 font-medium">{s.student_name}</td>
                      <td className="text-center px-5 py-3">{s.total_days}</td>
                      <td className="text-center px-5 py-3 text-emerald-600 font-medium">{s.present}</td>
                      <td className="text-center px-5 py-3 text-red-600 font-medium">{s.absent}</td>
                      <td className="text-center px-5 py-3">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${
                          s.percentage >= 90 ? 'bg-emerald-100 text-emerald-700' :
                          s.percentage >= 75 ? 'bg-amber-100 text-amber-700' :
                          s.percentage >= 50 ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {s.percentage}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};



export default NewTeacherDashboard;